// src/pages/ArticlePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import ArticleDetail from "../components/ArticleDetail";
import CategoryList from "../components/CategoryList";
import TagList from "../components/TagList";
import { Helmet } from "react-helmet-async";

const API_BASE = process.env.REACT_APP_API_BASE || "http://192.168.1.89:8000/api";
const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
const ArticlePage = () => {
  const { lang, slug } = useParams();

  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setFetchError(null);

      try {
        const safeSlug = encodeURIComponent(slug || "");
        const url = `${API_BASE}/articles/${safeSlug}/`;

        const res = await axios.get(url, {
          params: { lang },
          signal: controller.signal,
        });

        if (cancelled) return;

        const data = res.data ?? {};
        setArticle(data);
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setTags(Array.isArray(data.tags) ? data.tags : []);
      } catch (err) {
        if (
          err &&
          (err.name === "CanceledError" ||
            err.code === "ERR_CANCELED" ||
            String(err.message).toLowerCase().includes("canceled"))
        ) {
          return;
        }

        if (err && err.response && err.response.status === 404) {
          setFetchError(new Error("Article introuvable."));
        } else {
          setFetchError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {}

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [slug, lang]);

  const seoDescription = useMemo(() => {
    const raw = article?.excerpt || article?.content || "";
    return String(raw)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);
  }, [article]);

  const seoImage = article?.image || article?.image_url || "";
  const pageTitle = `${article?.title || "Article"} | 24News`;

  if (loading) return <p>Chargement…</p>;
  if (fetchError) return <p className="text-danger">Erreur : {fetchError.message}</p>;
  if (!article) return <p>Article introuvable.</p>;

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: lang || "en",
          dir: lang === "ar" ? "rtl" : "ltr",
        }}
      >
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={seoDescription || article?.title || "24News"}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={article?.title || "24News"} />
        <meta
          property="og:description"
          content={seoDescription || article?.title || "24News"}
        />
        {seoImage ? <meta property="og:image" content={seoImage} /> : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article?.title || "24News"} />
        <meta
          name="twitter:description"
          content={seoDescription || article?.title || "24News"}
        />
        {seoImage ? <meta name="twitter:image" content={seoImage} /> : null}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article?.title || "",
            description: seoDescription || "",
            image: seoImage || undefined,
            datePublished: article?.created_at || undefined,
            author: article?.author_name
              ? {
                  "@type": "Person",
                  name: article.author_name,
                }
              : undefined,
            mainEntityOfPage: window.location.href,
          })}
        </script>
        <link rel="alternate" hrefLang="en" href={`${baseUrl}/en/articles/${slug}`} />
        <link rel="alternate" hrefLang="fr" href={`${baseUrl}/fr/articles/${slug}`} />
        <link rel="alternate" hrefLang="ar" href={`${baseUrl}/ar/articles/${slug}`} />
        <link rel="alternate" hrefLang="de" href={`${baseUrl}/de/articles/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en/articles/${slug}`} />
        <link rel="canonical" href={`${window.location.origin}/${lang}/articles/${slug}`} />        
      </Helmet>

      <ArticleDetail
        article={article}
        adSlots={{}}
      />

      <div className="my-4">
        <CategoryList categories={categories} />
        <div className="mt-3">
          <TagList tags={tags} />
        </div>
      </div>
    </>
  );
};

export default ArticlePage;
